import { derivedFormFields } from './formDerivedFields';

// Selectable answer fields for the adaptive-journeys condition editor.
//
// Turns the forms loaded for a page (the shape `getFormsByPath` returns —
// see src/graphQlQueries/queries/forms/getFormsByPath.js: a list of
// { form_id, name, form_data }) into a flat list of fields an editor can
// build a condition on: { path, formId, formName, fieldId, label, type,
// options? }, where `path` is the json-logic var path a submitted answer
// is stored under ("form.<formId>.<fieldId>").
//
// INPUT_BLOCK_TYPES and the label-fallback logic below intentionally
// duplicate, rather than import, `INPUT_BLOCK_TYPES` (src/editor-v2/forms/
// types.ts) and `fieldLabel` (src/editor-v2/forms/logicEditor.helpers.ts).
// Those files are TypeScript; this module lives in Vue-2/Jest territory,
// and this project's Jest preset (node_modules/@vue/cli-plugin-unit-jest/
// presets/default/jest-preset.js) only registers .js/.jsx/.vue for module
// resolution and transform — no 'ts' extension, no ts transform. Importing
// from a .ts file here fails module resolution ("Could not locate module").
// To keep the two lists from silently drifting apart without adding a
// runtime dependency, tests/unit/answerFields.spec.js reads types.ts as
// TEXT and asserts every type named in its INPUT_BLOCK_TYPES literal is
// also present in the set below (exported for that comparison).
export const INPUT_BLOCK_TYPES = new Set([
  'text', 'textarea', 'email', 'phone', 'number', 'url',
  'radio', 'checkbox', 'select', 'boolean', 'rating', 'nps', 'consent', 'slider',
]);

// Mirrors `fieldLabel` in src/editor-v2/forms/logicEditor.helpers.ts:
// label in the requested language, falling back to the block id so a
// field is always selectable even when a translation is missing.
function fieldLabel(block, language) {
  return (block.label && block.label[language]) || block.id;
}

function optionLabel(option, language) {
  return (option.label && option.label[language]) || option.value;
}

function collectFormFields(form, language, includeDerived) {
  const doc = form.form_data;
  if (!doc || !Array.isArray(doc.pages)) return [];

  const fields = [];
  doc.pages.forEach((page) => {
    (page.blocks || []).forEach((block) => {
      if (!INPUT_BLOCK_TYPES.has(block.type)) return; // display blocks carry no answer

      const field = {
        path: `form.${form.form_id}.${block.id}`,
        formId: form.form_id,
        formName: form.name,
        fieldId: block.id,
        label: fieldLabel(block, language),
        type: block.type,
      };
      if (block.options) {
        field.options = block.options.map((option) => ({
          value: option.value,
          label: optionLabel(option, language),
        }));
      }
      fields.push(field);
    });
  });
  if (includeDerived && fields.length > 0) {
    derivedFormFields(doc).forEach((derived) => {
      fields.push({
        path: `form.${form.form_id}.${derived.fieldId}`,
        formId: form.form_id,
        formName: form.name,
        fieldId: derived.fieldId,
        label: derived.label,
        type: derived.fieldType,
        derived: derived.derived,
      });
    });
  }
  return fields;
}

// `includeDerived` appends, per form, the fields a reader cannot answer but a
// condition can ask about: calculated variables and "Formular abgeschickt"
// (see formDerivedFields.js). Every caller today is a CONDITION surface and
// sets it; a caller that lists what a reader answers must leave it off.
export function collectAnswerFields(formsData, language, includeDerived) {
  if (!Array.isArray(formsData)) return [];
  return formsData.flatMap((form) => collectFormFields(form, language, includeDerived === true));
}
